import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Alert,
  MenuItem,
  FormHelperText,
  Divider,
  Tabs,
  Tab,
  Paper,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { ArrowBack, Save, CloudUpload, ColorLens, Notifications, Assignment, Delete, Image, Business, Work } from "@mui/icons-material";
import TabsNav from "../../../Components/Common/TabsNav";
import OrganizationConfigService from "../../../services/OrganizationConfigService";
import { showNotification } from "../../../utils/notifications";
import AppService from "../../../services/AppService";
import OrgConfigService from "../../../services/OrganizationConfigService";
import SelfAssignmentSetting from "./SelfAssignmentSetting";
import NotificationSettings from "./NotificationSettings";
import DepartmentsSettings from "./DepartmentsSettings";
import PositionsSettings from "./PositionsSettings";
import OrganizationBankMapping from "./OrganizationBankMapping";
import { AccountBalance as BankIcon, Category as CategoryIcon } from "@mui/icons-material";
import { getActiveBusinessCategories } from "../../../mockData/orgDocBusinessCategories";

// Helper to decode JWT and extract organization id and roles
const getOrgInfoFromToken = () => {
  try {
    const authInfo = localStorage.getItem('authInfo');
    const token = authInfo ? JSON.parse(authInfo)?.token : null;
    if (!token) return null;
    const [, payloadB64] = token.split('.') || [];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64));
    return {
      orgId: payload?.org || payload?.organizationId || null,
      roles: (payload?.roles || []).map(r => r.authority || r).concat(payload?.authorities || [])
    };
  } catch (e) {
    console.error('Failed to extract org from token', e);
    return null;
  }
};
/**
 * Organization Configuration Form component
 * Used for creating and editing organization configurations
 */
const OrganizationConfigForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tokenInfo = getOrgInfoFromToken();
  const roles = tokenInfo?.roles || [];
  const isRootAdmin = roles.includes('ROLE_ROOT_ADMIN');
  
  // Form states
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    organizationId: "",
    logo: "",
    primaryColor: "#1976d2",
    secondaryColor: "#9c27b0",
    allowSelfAssign: false,
    allowTicketSelfAssign: false,
    allowOrgUserAssignment: false,
    selfAssignLimit: 5,
    notificationChannels: [],
    notificationEvents: [],
    allowWorkflowSelfAssign: false,
    businessCategoryId: "",
    organizationType: "MAINLAND"
  });

  // Get business categories for dropdown
  const businessCategories = getActiveBusinessCategories();
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  
  // Fetch organization config data on component mount
  useEffect(() => {
    if (isEdit && id) {
      // If we were navigated with a preloaded config (e.g., CUSTOMER_ADMIN flow), use it directly
      const preloaded = location?.state?.config;
      if (preloaded && preloaded.id === id) {
        setFormData(prev => ({
          ...prev,
          ...preloaded,
          name: preloaded?.organizationName || prev.name || "",
          organizationId: preloaded?.organizationId || prev.organizationId || ""
        }));
        setLoading(false);
        return;
      }
      // Fallback: fetch by ID
      OrganizationConfigService.getOrganizationConfigById(id)
        .then(data => {
          setFormData(prev => ({
            ...prev,
            ...data,
            // Auto-fill name from backend response field
            name: data?.organizationName || prev.name || "",
            organizationId: data?.organizationId || prev.organizationId || ""
          }));
          setLoading(false);
        })
        .catch(error => {
          console.error("Error fetching organization config:", error);
          setError("Failed to load organization configuration. Please try again.");
          setLoading(false);
        });
    } else {
      // initialize organizationId from query param (preferred) or token for create flow
      const queryOrgId = searchParams.get('organizationId');
      const info = getOrgInfoFromToken();
      const orgId = queryOrgId || info?.orgId || "";
      setFormData(prev => ({
        ...prev,
        organizationId: prev.organizationId || orgId
      }));
      // If we have the orgId, fetch organisation details to auto-fill name
      if (orgId) {
        AppService.get(`/api/organisations/details?organisationId=${orgId}`)
          .then(res => {
            if (res?.name) {
              setFormData(prev => ({
                ...prev,
                name: res.name
              }));
            }
          })
          .catch(err => {
            console.warn('Failed to fetch organisation details for name auto-fill', err);
          });
      }
      setLoading(false);
    }
  }, [isEdit, id, searchParams]);

  // Fetch logo when organizationId becomes available
  useEffect(() => {
    const orgId = formData.organizationId;
    if (!orgId) return;
    OrgConfigService.getOrganizationLogo(orgId)
      .then((arrayBuffer) => {
        if (!arrayBuffer) return;
        const blob = new Blob([arrayBuffer], { type: "image/*" });
        const url = URL.createObjectURL(blob);
        setLogoPreviewUrl(url);
      })
      .catch(() => {
        setLogoPreviewUrl("");
      });
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.organizationId]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle form input changes
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };
  
  // Validate form inputs
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.organizationId) {
      newErrors.organizationId = "Organization ID is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    setError("");
    
    // Prepare data to send to API
    // Ensure organizationId present (derive from token if missing)
    const info = getOrgInfoFromToken();
    const resolvedOrgId = formData.organizationId || info?.orgId || "";
    // Build payload
    let dataToSend = {
      ...formData,
      organizationId: resolvedOrgId,
      allowOrgUserAssignment: formData.allowOrgUserAssignment
    };
    // Only ROOT_ADMIN may include self-assign fields in the main form submit
    const roles = info?.roles || [];
    const isRootAdmin = roles.includes('ROLE_ROOT_ADMIN');
    if (isRootAdmin) {
      dataToSend = {
        ...dataToSend,
        description: formData.description || null
      };
    }

    // Ensure we don't accidentally send self-assignment fields from this form.
    // Self-assignment is managed in the SelfAssignmentSetting tab and endpoints.
    delete dataToSend.allowSelfAssign;
    delete dataToSend.allowTicketSelfAssign;
    delete dataToSend.selfAssignLimit;
    delete dataToSend.allow_workflow_self_assign;
    
    // Ensure we don't accidentally send notification fields from this form.
    // Notifications are managed in the NotificationSettings tab and endpoints.
    delete dataToSend.notificationChannels;
    delete dataToSend.notificationEvents;
    
    console.log("Saving organization config with real data:", dataToSend);
    
    let savePromise;
    
    if (isEdit && id) {
      savePromise = OrganizationConfigService.updateOrganizationConfig(id, dataToSend);
    } else {
      // Upsert by organizationId (server will create if missing)
      savePromise = OrganizationConfigService.updateByOrganizationId(resolvedOrgId, dataToSend);
    }
    
    savePromise
      .then(() => {
        setSaving(false);
        showNotification(
          `Organization configuration ${isEdit ? "updated" : "created"} successfully`,
          "success"
        );
        navigate("/admin/organization-config");
      })
      .catch(error => {
        console.error(`Error ${isEdit ? "updating" : "creating"} configuration:`, error);
        setError(error?.response?.data?.message || `Failed to ${isEdit ? "update" : "create"} configuration`);
        setSaving(false);
      });
  };
  
  
  // Handle logo upload (real API)
  const handleLogoUpload = async (event) => {
    try {
      if (!event.target.files || !event.target.files[0]) return;
      const file = event.target.files[0];
      if (!formData.organizationId) {
        showNotification("Organization ID is missing", "error");
        return;
      }
      await OrgConfigService.uploadOrganizationLogo(formData.organizationId, file);
      // Refresh preview
      const arrayBuffer = await OrgConfigService.getOrganizationLogo(formData.organizationId);
      if (arrayBuffer) {
        const blob = new Blob([arrayBuffer], { type: file.type || "image/*" });
        const url = URL.createObjectURL(blob);
        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
        setLogoPreviewUrl(url);
        showNotification("Logo uploaded", "success");
      }
    } catch (e) {
      console.error("Logo upload failed", e);
      showNotification("Logo upload failed", "error");
    }
  };

  const handleLogoDelete = async () => {
    try {
      if (!formData.organizationId) return;
      await OrgConfigService.deleteOrganizationLogo(formData.organizationId);
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl("");
      showNotification("Logo deleted", "success");
    } catch (e) {
      console.error("Logo delete failed", e);
      showNotification("Logo delete failed", "error");
    }
  };
  
  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            {isEdit ? "Edit Organization Configuration" : "Organization Settings"}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/admin/organization-config")}
          >
            Back to List
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TabsNav
              value={activeTab}
              onChange={(v) => handleTabChange(null, v)}
              items={[
                { label: 'General', icon: <Assignment /> },
                { label: 'Self-Assignment', icon: <Assignment /> },
                { label: 'Branding', icon: <ColorLens /> },
                { label: 'Notifications', icon: <Notifications /> },
                { label: 'Departments', icon: <Business /> },
                { label: 'Positions', icon: <Work /> },
                ...(isRootAdmin ? [{ label: 'Bank Accounts', icon: <BankIcon /> }] : []),
              ]}
            />
            <Divider sx={{ borderColor: 'divider', mb: 2 }} />
            
            {/* General Tab */}
            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Organization Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    required
                    disabled={!isRootAdmin}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Organization ID"
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    disabled={!isRootAdmin}
                    fullWidth
                    margin="normal"
                    error={Boolean(errors.organizationId)}
                    helperText={errors.organizationId || "Unique identifier for the organization"}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                    disabled={!isRootAdmin}
                  />
                </Grid>

                {/* Organization Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Organization Type</InputLabel>
                    <Select
                      name="organizationType"
                      value={formData.organizationType}
                      label="Organization Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="MAINLAND">Mainland Company</MenuItem>
                      <MenuItem value="FREE_ZONE">Free Zone Company</MenuItem>
                      <MenuItem value="OFFSHORE">Offshore Company</MenuItem>
                    </Select>
                    <FormHelperText>Type of company registration</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Business Category */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Business Category</InputLabel>
                    <Select
                      name="businessCategoryId"
                      value={formData.businessCategoryId}
                      label="Business Category"
                      onChange={handleChange}
                    >
                      <MenuItem value="">
                        <em>Select Business Category</em>
                      </MenuItem>
                      {businessCategories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 0.5,
                                backgroundColor: `${category.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px'
                              }}
                            >
                              <CategoryIcon sx={{ fontSize: 16, color: category.color }} />
                            </Box>
                            <span>{category.name}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Industry classification for document requirements</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Show selected category info */}
                {formData.businessCategoryId && (
                  <Grid item xs={12}>
                    {(() => {
                      const selectedCategory = businessCategories.find(c => c.id === formData.businessCategoryId);
                      if (!selectedCategory) return null;
                      const requiredDocs = selectedCategory.requiredDocuments || [];
                      const mandatoryCount = requiredDocs.filter(d => d.isMandatory).length;
                      return (
                        <Alert
                          severity="info"
                          icon={<CategoryIcon />}
                          sx={{
                            backgroundColor: `${selectedCategory.color}10`,
                            '& .MuiAlert-icon': { color: selectedCategory.color }
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {selectedCategory.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedCategory.description}
                          </Typography>
                          {mandatoryCount > 0 && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              <strong>{mandatoryCount} mandatory industry-specific documents</strong> will be required
                            </Typography>
                          )}
                        </Alert>
                      );
                    })()}
                  </Grid>
                )}
              </Grid>
            )}
            
            {/* Self-Assignment Tab */}
            {activeTab === 1 && (
              <SelfAssignmentSetting organizationId={formData.organizationId} />
            )}
            
            {/* Branding Tab */}
            {activeTab === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Primary Color"
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: formData.primaryColor,
                              border: "1px solid rgba(0, 0, 0, 0.23)"
                            }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Secondary Color"
                    name="secondaryColor"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: formData.secondaryColor,
                              border: "1px solid rgba(0, 0, 0, 0.23)"
                            }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Image fontSize="small" style={{ marginRight: 8 }} /> Organization Logo
                    </Typography>
                    <Box>
                      <input
                        accept="image/png, image/jpeg, image/svg+xml"
                        style={{ display: "none" }}
                        id="logo-upload"
                        type="file"
                        onChange={handleLogoUpload}
                      />
                      <label htmlFor="logo-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          sx={{ mr: 1 }}
                          disabled={!formData.organizationId}
                        >
                          Upload
                        </Button>
                      </label>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleLogoDelete}
                        disabled={!logoPreviewUrl}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                  <Box mt={2} display="flex" justifyContent="center" minHeight={200} alignItems="center" border="1px dashed #ccc" borderRadius={1}>
                    {logoPreviewUrl ? (
                      <img
                        src={logoPreviewUrl}
                        alt="Organization Logo"
                        style={{ maxWidth: "100%", maxHeight: 200 }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No logo uploaded
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
            
            {/* Notifications Tab */}
            {activeTab === 3 && (
              <NotificationSettings organizationId={formData.organizationId} />
            )}
            
            {/* Departments Tab */}
            {activeTab === 4 && (
              <DepartmentsSettings organizationId={formData.organizationId} />
            )}
            
            {/* Positions Tab */}
            {activeTab === 5 && (
              <PositionsSettings organizationId={formData.organizationId} />
            )}

            {/* Bank Accounts Tab - Only for Root Admin */}
            {isRootAdmin && activeTab === 6 && (
              <OrganizationBankMapping organizationId={formData.organizationId} />
            )}

            {/* Only show Save Configuration button for General, and Branding tabs */}
            {(activeTab === 0 || activeTab === 2) && (
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Configuration"}
                </Button>
              </Box>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationConfigForm;
